import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'

const MAX_FILE_BYTES = 10 * 1024 * 1024
const ACCEPTED = ['.pdf', '.doc', '.docx']

export function StatusChangeModal({
  show,
  target,
  loading,
  onClose,
  onConfirm,
}: {
  show: boolean
  target: 'rejected' | 'hired' | null
  loading: boolean
  onClose: () => void
  onConfirm: (payload: { notes?: string; offerLetter?: File }) => void
}) {
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  // Reset while rendering (not by remounting — see ReminderTaskModal).
  const [lastShow, setLastShow] = useState(show)
  if (show !== lastShow) {
    setLastShow(show)
    if (show) {
      setNotes('')
      setFile(null)
      setFileError(null)
    }
  }

  const isReject = target === 'rejected'

  const pickFile = (picked: File | null) => {
    setFile(null)
    setFileError(null)
    if (!picked) return
    const ext = picked.name.slice(picked.name.lastIndexOf('.')).toLowerCase()
    if (!ACCEPTED.includes(ext)) {
      setFileError('Only PDF, DOC or DOCX files are allowed.')
      return
    }
    if (picked.size > MAX_FILE_BYTES) {
      setFileError('File must be 10 MB or smaller.')
      return
    }
    setFile(picked)
  }

  const canSubmit = isReject ? notes.trim().length > 0 : true

  const submit = () => {
    if (!canSubmit) return
    onConfirm(isReject ? { notes: notes.trim() } : { offerLetter: file ?? undefined })
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isReject ? 'Reject candidate' : 'Mark as hired'}
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isReject ? 'danger' : 'primary'}
            onClick={submit}
            loading={loading}
            disabled={!canSubmit}
          >
            {isReject ? 'Reject' : 'Mark as Hired'}
          </Button>
        </div>
      }
    >
      {isReject ? (
        <>
          <label htmlFor="reject-reason" className="form-label fs-13 fw-medium">
            Reason for rejection <span className="text-danger">*</span>
          </label>
          <textarea
            id="reject-reason"
            className="form-control"
            rows={4}
            maxLength={2000}
            placeholder="e.g. Required experience with Laravel not met."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <p className="fs-12 text-muted mb-0 mt-1">
            Saved in the application's status history.
          </p>
        </>
      ) : (
        <>
          <label htmlFor="offer-letter" className="form-label fs-13 fw-medium">
            Offer letter <span className="text-muted fs-12 fw-normal">(optional)</span>
          </label>
          <input
            id="offer-letter"
            type="file"
            className={`form-control${fileError ? ' is-invalid' : ''}`}
            accept={ACCEPTED.join(',')}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          {fileError ? (
            <div className="invalid-feedback">{fileError}</div>
          ) : (
            <p className="fs-12 text-muted mb-0 mt-1">PDF, DOC or DOCX, up to 10 MB. You can upload one later from the candidate's profile.</p>
          )}
        </>
      )}
    </Modal>
  )
}
